<template>
    <div class="space-y-5">
        <publications-nav v-if="profile" :profile="profile" :dashboard="dashboard" />

        <section class="publication-controls" aria-label="Пошук і впорядкування дискусій">
            <div
                class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-3 sm:flex sm:flex-col sm:items-stretch"
                data-discussion-toolbar
            >
                <div class="contents sm:flex sm:items-center sm:gap-3" data-discussion-toolbar-row="primary">
                    <label class="col-span-2 row-start-1 min-w-0 sm:flex-1" data-discussion-search>
                        <span class="sr-only">Пошук у дискусіях</span>
                        <input
                            v-model="searchQuery"
                            type="search"
                            maxlength="100"
                            class="foxy-input w-full !px-3 !py-2 text-sm"
                            placeholder="Пошук за заголовком або текстом..."
                            @input="scheduleSearch"
                            @keydown.enter.prevent="runSearchNow"
                        />
                    </label>
                    <label class="col-start-2 row-start-2 w-[6.5rem] shrink-0 sm:w-32" data-discussion-sort>
                        <span class="sr-only">Порядок дискусій</span>
                        <select v-model="sortOrder" class="foxy-select w-full !py-2 !pl-3 !pr-9 text-sm" @change="reloadArchive">
                            <option value="newest">Новіші</option>
                            <option value="oldest">Старіші</option>
                        </select>
                    </label>
                    <a
                        v-if="isOwner"
                        href="/discussions/create"
                        class="acc-btn-primary col-span-2 row-start-3 shrink-0 justify-self-start whitespace-nowrap text-center text-sm"
                        data-discussion-create
                        >Розпочати дискусію</a
                    >
                </div>
                <div
                    class="col-start-1 row-start-2 flex min-w-0 flex-wrap items-center gap-1"
                    role="group"
                    aria-label="Стан дискусії"
                    data-discussion-toolbar-row="states"
                >
                    <button
                        v-for="option in stateOptions"
                        :key="option.value"
                        type="button"
                        class="publication-filter-chip"
                        :class="{ 'is-active': stateFilter === option.value }"
                        :aria-pressed="stateFilter === option.value ? 'true' : 'false'"
                        @click="setState(option.value)"
                    >
                        {{ option.label }}
                    </button>
                </div>
            </div>
            <div v-if="searchPending || filtersActive" class="mt-2 text-xs text-foxy-text-secondary" aria-live="polite">
                <span v-if="searchPending || (loading && searchQuery.trim())">Шукаємо…</span>
                <span v-else-if="!loading && !loadError">Знайдено: {{ meta.total }}</span>
            </div>
        </section>

        <div v-if="loading && !discussionsLocal.length" class="space-y-3" aria-label="Завантаження дискусій">
            <div v-for="index in 3" :key="index" class="acc-glass animate-pulse rounded-2xl border border-slate-200/70 p-5 dark:border-white/10">
                <div class="h-5 w-2/3 rounded bg-slate-200 dark:bg-white/10" />
                <div class="mt-3 h-4 w-full rounded bg-slate-200 dark:bg-white/10" />
                <div class="mt-2 h-4 w-4/5 rounded bg-slate-200 dark:bg-white/10" />
            </div>
        </div>

        <div
            v-else-if="loadError"
            class="acc-glass rounded-2xl border border-red-300/70 p-5 text-sm text-red-700 dark:border-red-400/30 dark:text-red-200"
        >
            <p>{{ loadError }}</p>
            <button type="button" class="acc-btn mt-3" @click="reloadArchive">Спробувати ще раз</button>
        </div>

        <div v-else-if="!discussionsLocal.length" class="acc-glass rounded-2xl border border-slate-200/70 p-6 text-center dark:border-white/10">
            <div class="font-bold">{{ emptyTitle }}</div>
            <p class="mt-1 text-sm text-slate-600 dark:text-white/65">{{ emptyDescription }}</p>
            <a v-if="isOwner && !searchQuery.trim() && stateFilter === 'all'" href="/discussions/create" class="acc-btn-primary mt-4 inline-flex"
                >Розпочати дискусію</a
            >
        </div>

        <div v-else class="space-y-3">
            <article
                v-for="item in discussionsLocal"
                :key="item.id"
                :ref="`card-${item.id}`"
                class="publication-discussion-card cursor-pointer transition hover:border-foxy-action/45 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                role="link"
                tabindex="0"
                :aria-label="`Відкрити дискусію: ${item.title}`"
                data-discussion-card
                @click="navigateDiscussion(item, $event)"
                @keydown="handleCardKeydown(item, $event)"
            >
                <div class="flex items-start justify-between gap-3" data-discussion-header>
                    <a
                        :href="item.url"
                        class="min-w-0 flex-1 break-words text-base font-extrabold leading-snug text-foxy-text transition hover:text-foxy-action sm:text-lg"
                        @click.stop
                        @keydown.stop
                        >{{ item.title }}</a
                    >
                    <span
                        v-if="isOwner && moderationLabel(item)"
                        class="acc-pill shrink-0"
                        :class="moderationClass(item)"
                        :data-discussion-moderation-state="item.moderation_status"
                        >{{ moderationLabel(item) }}</span
                    >
                    <span
                        v-else
                        :class="[
                            'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold',
                            item.is_closed
                                ? 'border-foxy-border bg-foxy-elevated text-foxy-text-secondary'
                                : 'border-foxy-success/55 bg-foxy-success/15 text-foxy-text',
                        ]"
                        :data-discussion-state="item.is_closed ? 'closed' : 'open'"
                        data-discussion-status
                    >
                        <span
                            :class="['h-1.5 w-1.5 rounded-full', item.is_closed ? 'bg-foxy-text-secondary/60' : 'bg-foxy-success']"
                            aria-hidden="true"
                        />
                        {{ item.is_closed ? 'Закрита' : 'Відкрита' }}
                    </span>
                </div>
                <p
                    class="mt-3 line-clamp-3 whitespace-pre-line break-words text-sm leading-6 text-foxy-text-secondary"
                    v-text="item.preview || 'Без опису'"
                />
                <div v-if="item.work" class="mt-3 flex min-w-0 items-center gap-2 text-sm text-foxy-text-secondary">
                    <i class="fas fa-book text-xs" aria-hidden="true" />
                    <a
                        :href="item.work.url"
                        class="min-w-0 truncate hover:text-foxy-action hover:underline"
                        data-discussion-work
                        @click.stop
                        @keydown.stop
                        >{{ item.work.title }}</a
                    >
                    <span
                        v-if="item.work.additional_count"
                        class="shrink-0 text-xs"
                        :aria-label="`Інші пов’язані книги: ${item.work.additional_count}`"
                        >+{{ item.work.additional_count }}</span
                    >
                </div>

                <div
                    v-if="isOwner && item.moderation_reason"
                    class="mt-3 rounded-xl border border-red-300/60 bg-red-50/70 px-3 py-2 text-xs text-red-800 dark:border-red-400/25 dark:bg-red-500/10 dark:text-red-200"
                >
                    Причина: {{ item.moderation_reason }}
                </div>

                <div class="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-foxy-border/70 pt-3">
                    <div class="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-foxy-text-secondary" data-discussion-footer-meta>
                        <i class="far fa-comment" aria-hidden="true" />
                        <span>{{ replyCountLabel(item.replies_count) }}</span>
                        <span v-if="item.displayed_at" aria-hidden="true">·</span>
                        <time v-if="item.displayed_at" :datetime="item.displayed_at">{{ item.displayed_at_human }}</time>
                    </div>
                    <div class="ml-auto flex shrink-0 items-center gap-1" data-discussion-actions>
                        <template v-if="isOwner">
                            <a
                                v-if="item.capabilities.can_edit"
                                :href="`/discussions/${item.slug}/edit`"
                                class="acc-btn min-h-11 !px-3 !py-1.5 text-xs"
                                data-discussion-edit
                                @click.stop
                                @keydown.stop
                                >Редагувати</a
                            >
                            <div
                                v-if="hasOverflowActions(item)"
                                :ref="`overflow-${item.id}`"
                                class="relative inline-block"
                                data-discussion-overflow
                                @click.stop
                                @keydown.stop
                                @focusout="handleOverflowFocusOut($event)"
                            >
                                <button
                                    :ref="`trigger-${item.id}`"
                                    type="button"
                                    class="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-full p-0 text-foxy-text-secondary transition-colors hover:bg-foxy-elevated hover:text-foxy-text focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20"
                                    :aria-label="`Інші дії: ${item.title}`"
                                    title="Інші дії"
                                    aria-haspopup="menu"
                                    :aria-expanded="activeMenuId === item.id ? 'true' : 'false'"
                                    :aria-controls="`discussion-menu-${item.id}`"
                                    :disabled="busyActionId !== null || deleting || confirmingDelete"
                                    data-discussion-overflow-trigger
                                    @click.stop="toggleOverflow(item, $event)"
                                    @keydown.stop="handleOverflowTriggerKeydown(item, $event)"
                                >
                                    <span class="text-xl leading-none" aria-hidden="true">⋮</span>
                                </button>
                                <div
                                    v-if="activeMenuId === item.id"
                                    :id="`discussion-menu-${item.id}`"
                                    :ref="`menu-${item.id}`"
                                    class="discussion-action-menu"
                                    :style="menuStyle"
                                    role="menu"
                                    :aria-label="`Дії дискусії: ${item.title}`"
                                    @click.stop
                                    @keydown.stop="handleOverflowMenuKeydown($event)"
                                >
                                    <button
                                        v-if="item.capabilities.can_close"
                                        type="button"
                                        role="menuitem"
                                        :disabled="busyActionId !== null || deleting || confirmingDelete"
                                        @click="changeConversationState(item, 'close')"
                                    >
                                        <i class="fas fa-lock" aria-hidden="true" />Закрити дискусію
                                    </button>
                                    <button
                                        v-if="item.capabilities.can_reopen"
                                        type="button"
                                        role="menuitem"
                                        :disabled="busyActionId !== null || deleting || confirmingDelete"
                                        @click="changeConversationState(item, 'reopen')"
                                    >
                                        <i class="fas fa-lock-open" aria-hidden="true" />Відкрити знову
                                    </button>
                                    <button
                                        v-if="item.capabilities.can_delete"
                                        type="button"
                                        role="menuitem"
                                        class="text-foxy-error"
                                        :disabled="busyActionId !== null || deleting || confirmingDelete"
                                        @click="deleteDiscussion(item)"
                                    >
                                        <i class="fas fa-trash" aria-hidden="true" />Видалити
                                    </button>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </article>
        </div>

        <div v-if="meta.has_more && !loadError" class="flex justify-center pt-1">
            <button
                type="button"
                class="acc-btn-primary min-w-44"
                :disabled="loading || loadingMore || searchPending || busyActionId !== null || deleting"
                @click="loadMore"
            >
                {{ loadingMore ? 'Завантаження…' : 'Показати ще' }}
            </button>
        </div>
    </div>
</template>

<script lang="js">
import axios from 'axios';
import PublicationsNav from '../components/PublicationsNav.vue';

export default {
    name: 'ProfileDiscussions',
    components: { PublicationsNav },
    props: {
        profile: { type: Object, default: null },
        publicationContext: { type: Object, default: null },
        dashboard: { type: Object, default: null },
        isOwner: { type: Boolean, default: false },
    },
    data() {
        return {
            discussionsLocal: [],
            counts: { total: 0, open: 0, closed: 0 },
            meta: { current_page: 1, last_page: 1, per_page: 20, total: 0, has_more: false },
            searchQuery: '',
            stateFilter: 'all',
            sortOrder: 'newest',
            loading: false,
            loadingMore: false,
            loadError: '',
            searchPending: false,
            searchTimer: null,
            requestSequence: 0,
            busyActionId: null,
            deleting: false,
            deletingId: null,
            confirmingDelete: false,
            actionModal: null,
            activeMenuId: null,
            menuStyle: { visibility: 'hidden' },
            stateOptions: [
                { value: 'all', label: 'Усі' },
                { value: 'open', label: 'Відкриті' },
                { value: 'closed', label: 'Закриті' },
            ],
        };
    },
    computed: {
        filtersActive() {
            return Boolean(this.searchQuery.trim()) || this.stateFilter !== 'all';
        },
        emptyTitle() {
            if (this.searchQuery.trim()) return 'За цим пошуком нічого не знайдено.';
            if (this.stateFilter === 'open') return 'Відкритих дискусій не знайдено.';
            if (this.stateFilter === 'closed') return 'Закритих дискусій не знайдено.';
            return 'У профілі поки немає дискусій.';
        },
        emptyDescription() {
            if (this.searchQuery.trim()) return 'Спробуйте інший запит або очистьте пошук.';
            if (this.stateFilter !== 'all') return 'Оберіть інший фільтр, щоб переглянути архів.';
            return this.isOwner ? 'Створіть першу дискусію — вона з’явиться тут після збереження.' : 'У цьому профілі поки немає доступних дискусій.';
        },
    },
    watch: {
        'profile.username': {
            immediate: true,
            handler(username) {
                if (username) this.reloadArchive();
            },
        },
    },
    mounted() {
        document.addEventListener('click', this.handleOverflowOutsideClick, true);
        document.addEventListener('keydown', this.handleOverflowEscape, true);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleOverflowOutsideClick, true);
        document.removeEventListener('keydown', this.handleOverflowEscape, true);
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.requestSequence += 1;
    },
    methods: {
        navigateDiscussion(item, event) {
            if (!item?.url || event?.defaultPrevented) return;
            if (event?.target?.closest?.('a, button, input, select, textarea, [role="menu"], [contenteditable="true"]')) return;
            if (window.getSelection?.()?.toString()) return;
            window.location.assign(item.url);
        },
        handleCardKeydown(item, event) {
            if (event.target !== event.currentTarget || !['Enter', ' '].includes(event.key)) return;
            event.preventDefault();
            if (!event.repeat) this.navigateDiscussion(item);
        },
        hasOverflowActions(item) {
            const actions = item?.capabilities || {};
            return this.isOwner && Boolean(actions.can_close || actions.can_reopen || actions.can_delete);
        },
        overflowRef(name, id = this.activeMenuId) {
            const ref = this.$refs?.[`${name}-${id}`];
            return Array.isArray(ref) ? ref[0] : ref;
        },
        overflowMenuItems() {
            return Array.from(this.overflowRef('menu')?.querySelectorAll('[role="menuitem"]:not(:disabled)') || []);
        },
        toggleOverflow(item, event, focusLast = false) {
            if (!this.hasOverflowActions(item) || this.busyActionId !== null || this.deleting || this.confirmingDelete || event?.detail > 1) return;
            if (this.activeMenuId === item.id) return this.closeOverflow(true);
            this.activeMenuId = item.id;
            this.menuStyle = { visibility: 'hidden' };
            this.$nextTick(() => {
                if (this.activeMenuId !== item.id) return;
                const trigger = this.overflowRef('trigger');
                const menu = this.overflowRef('menu');
                if (trigger && menu) {
                    const opensUp = trigger.getBoundingClientRect().bottom + menu.offsetHeight + 8 > window.innerHeight - 12;
                    this.menuStyle = {
                        visibility: 'visible',
                        top: opensUp ? 'auto' : 'calc(100% + 0.5rem)',
                        bottom: opensUp ? 'calc(100% + 0.5rem)' : 'auto',
                    };
                }
                this.$nextTick(() => {
                    if (this.activeMenuId !== item.id) return;
                    const items = this.overflowMenuItems();
                    (focusLast ? items[items.length - 1] : items[0])?.focus();
                });
            });
        },
        closeOverflow(restoreFocus = false) {
            const id = this.activeMenuId;
            this.activeMenuId = null;
            this.menuStyle = { visibility: 'hidden' };
            if (restoreFocus && id !== null) this.restoreOverflowFocus(id);
        },
        restoreOverflowFocus(id) {
            this.$nextTick(() => {
                const target =
                    this.overflowRef('trigger', id) ||
                    this.overflowRef('card', id) ||
                    this.$el?.querySelector('[data-discussion-card], input[type="search"]');
                target?.focus();
            });
        },
        handleOverflowOutsideClick(event) {
            if (this.activeMenuId === null || this.overflowRef('overflow')?.contains(event.target)) return;
            this.closeOverflow();
        },
        handleOverflowEscape(event) {
            if (event.key !== 'Escape' || this.activeMenuId === null) return;
            event.preventDefault();
            event.stopPropagation();
            this.closeOverflow(true);
        },
        handleOverflowFocusOut(event) {
            if (this.activeMenuId !== null && !this.overflowRef('overflow')?.contains(event.relatedTarget)) this.closeOverflow();
        },
        handleOverflowTriggerKeydown(item, event) {
            if (event.repeat && ['Enter', ' '].includes(event.key)) event.preventDefault();
            if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
            event.preventDefault();
            if (this.activeMenuId !== item.id) return this.toggleOverflow(item, undefined, event.key === 'ArrowUp');
            this.$nextTick(() => {
                const items = this.overflowMenuItems();
                (event.key === 'ArrowUp' ? items[items.length - 1] : items[0])?.focus();
            });
        },
        handleOverflowMenuKeydown(event) {
            const items = this.overflowMenuItems();
            if (!items.length) return;
            const current = Math.max(0, items.indexOf(document.activeElement));
            let next = null;
            if (event.key === 'ArrowDown') next = (current + 1) % items.length;
            if (event.key === 'ArrowUp') next = (current - 1 + items.length) % items.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = items.length - 1;
            if (next !== null) {
                event.preventDefault();
                items[next].focus();
            }
        },
        scheduleSearch() {
            this.invalidateContinuation();
            if (this.searchTimer) clearTimeout(this.searchTimer);
            this.searchPending = true;
            this.searchTimer = setTimeout(() => {
                this.searchTimer = null;
                this.searchPending = false;
                this.reloadArchive();
            }, 275);
        },
        runSearchNow() {
            if (this.searchTimer) clearTimeout(this.searchTimer);
            this.searchTimer = null;
            this.searchPending = false;
            this.reloadArchive();
        },
        setState(state) {
            if (this.stateFilter === state) return;
            this.stateFilter = state;
            this.reloadArchive();
        },
        reloadArchive() {
            this.closeOverflow();
            if (this.searchTimer) clearTimeout(this.searchTimer);
            this.searchTimer = null;
            this.searchPending = false;
            this.invalidateContinuation();
            return this.fetchArchive(1, false);
        },
        invalidateContinuation() {
            this.requestSequence += 1;
            this.loadingMore = false;
            this.meta = { ...this.meta, current_page: 1, has_more: false };
        },
        loadMore() {
            if (this.loading || this.searchPending || this.loadingMore || this.busyActionId !== null || this.deleting || !this.meta.has_more) return;
            return this.fetchArchive(this.meta.current_page + 1, true);
        },
        async fetchArchive(page = 1, append = false) {
            if (append && (this.loading || this.searchPending)) return;
            const username = this.profile?.username;
            if (!username) return;
            const requestId = ++this.requestSequence;
            this.loadError = '';
            if (append) this.loadingMore = true;
            else {
                this.loading = true;
                this.loadingMore = false;
            }

            try {
                const { data } = await axios.get(`/api/account/profile/${encodeURIComponent(username)}/discussions`, {
                    params: { q: this.searchQuery.trim() || undefined, state: this.stateFilter, sort: this.sortOrder, page },
                });
                if (requestId !== this.requestSequence) return;
                const incoming = Array.isArray(data?.data) ? data.data : [];
                if (append) {
                    const knownIds = new Set(this.discussionsLocal.map((item) => item.id));
                    this.discussionsLocal = [
                        ...this.discussionsLocal,
                        ...incoming.filter((item) => {
                            if (knownIds.has(item.id)) return false;
                            knownIds.add(item.id);
                            return true;
                        }),
                    ];
                } else {
                    this.discussionsLocal = incoming;
                }
                this.counts = { total: 0, open: 0, closed: 0, ...(data?.counts || {}) };
                this.meta = { current_page: page, last_page: page, per_page: 20, total: 0, has_more: false, ...(data?.meta || {}) };
            } catch (error) {
                if (requestId !== this.requestSequence) return;
                this.loadError = error?.response?.data?.message || 'Не вдалося завантажити архів дискусій.';
            } finally {
                if (requestId === this.requestSequence) {
                    this.loading = false;
                    this.loadingMore = false;
                }
            }
        },
        replyCountLabel(count) {
            const value = Number(count || 0);
            if (value % 10 === 1 && value % 100 !== 11) return `${value} відповідь`;
            if ([2, 3, 4].includes(value % 10) && ![12, 13, 14].includes(value % 100)) return `${value} відповіді`;
            return `${value} відповідей`;
        },
        moderationLabel(item) {
            if (item?.moderation_status === 'pending') return 'На перевірці';
            if (item?.moderation_status === 'blocked') return 'Заблокована';
            return '';
        },
        moderationClass(item) {
            return item?.moderation_status === 'blocked'
                ? '!border-red-300/60 !text-red-700 dark:!border-red-400/30 dark:!text-red-200'
                : '!border-amber-300/70 !text-amber-800 dark:!border-amber-400/30 dark:!text-amber-200';
        },
        async changeConversationState(item, action) {
            if (!item?.slug || this.busyActionId !== null || this.deleting || this.confirmingDelete) return;
            const capability = action === 'close' ? 'can_close' : 'can_reopen';
            if (!item.capabilities?.[capability]) return;
            if (!this.isOwner) return;
            const fromOverflow = this.activeMenuId === item.id;
            this.closeOverflow();
            this.invalidateContinuation();
            this.busyActionId = item.id;
            try {
                const { data } = await axios.post(
                    `/discussions/${item.slug}/${action}`,
                    {},
                    { headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } },
                );
                if (!data?.success) throw new Error(data?.message || 'Не вдалося змінити стан дискусії.');
                await this.reloadArchive();
            } catch (error) {
                alert(error?.response?.data?.message || error?.message || 'Помилка зміни стану дискусії.');
                await this.reloadArchive();
            } finally {
                this.busyActionId = null;
                if (fromOverflow) this.restoreOverflowFocus(item.id);
            }
        },
        async deleteDiscussion(item = null) {
            const target = item || this.actionModal;
            if (
                !this.isOwner ||
                !target?.capabilities?.can_delete ||
                !target?.slug ||
                this.busyActionId !== null ||
                this.deleting ||
                this.confirmingDelete
            )
                return;
            this.confirmingDelete = true;
            const context = { ...this.publicationContext, username: this.profile.username, family: 'discussion', id: target.id };
            let started = false;
            const fromOverflow = this.activeMenuId === target.id;
            this.closeOverflow();
            try {
                const confirmed = await window.confirm('Видалити цю дискусію? Дію неможливо скасувати.');
                if (!confirmed || this._isDestroyed || context.username !== this.profile?.username) return;
                started = true;
                this.$emit('publication-mutation-start', context);
                this.invalidateContinuation();
                this.deleting = true;
                this.deletingId = target.id || null;
                const { data } = await axios.delete(`/discussions/${target.slug}`, {
                    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                });
                if (data?.success !== true) throw new Error(data?.message || 'Не вдалося видалити дискусію.');
                if (this._isDestroyed || context.username !== this.profile?.username
                    || context.generation !== this.publicationContext?.generation) return;
                this.$emit('discussion-deleted', { ...context, discussionsCount: data.discussions_count });
                await this.reloadArchive();
                if (this.actionModal?.slug === target.slug) this.actionModal = null;
            } catch (error) {
                alert(error?.response?.data?.message || error?.message || 'Помилка видалення дискусії.');
                await this.reloadArchive();
            } finally {
                this.deleting = false;
                this.deletingId = null;
                this.confirmingDelete = false;
                if (fromOverflow) this.restoreOverflowFocus(target.id);
                if (started) {
                    if (context.finish) context.finish(context);
                    else this.$emit('publication-mutation-finish', context);
                }
            }
        },
    },
};
</script>

<style scoped>
.discussion-action-menu {
    position: absolute;
    right: 0;
    z-index: 30;
    min-width: 13rem;
    max-width: calc(100vw - 1.5rem);
    overflow: hidden;
    border: 1px solid rgb(var(--foxy-border));
    border-radius: 0.75rem;
    background: rgb(var(--foxy-surface));
    box-shadow: 0 14px 34px rgb(var(--foxy-cocoa) / 0.24);
}
.discussion-action-menu > button {
    display: flex;
    width: 100%;
    min-height: 2.75rem;
    align-items: center;
    gap: 0.65rem;
    padding: 0.7rem 0.85rem;
    color: rgb(var(--foxy-text-secondary));
    font-size: 0.8rem;
    font-weight: 700;
    text-align: left;
}
.discussion-action-menu > button:hover,
.discussion-action-menu > button:focus-visible {
    background: rgb(var(--foxy-elevated));
    color: rgb(var(--foxy-text));
    outline: none;
}
.discussion-action-menu > button.text-foxy-error {
    color: rgb(var(--foxy-error));
}
</style>
